import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import api from '../../utils/functions/api';
import { showAlert } from '../../utils/functions/globalDialog';
import {
  DEFAULT_MARKETING_CODES,
  setMarketingCodesCache,
} from '../../hooks/useMarketingCodePrefixes';
import { SettingSection } from './ProjectPrefixSettings';

// รหัสนำหน้าเลขเอกสารที่ระบบสร้างให้เอง — ว่าง = ค่าเดิม (ต้องตรงกับ backend/utils/numberPrefix.js)
const DOC_FIELDS = [
  { key: 'payment_number_prefix', label: 'Payment (เตรียมจ่าย)', fallback: 'STN', sample: (p) => `${p}00001` },
  { key: 'expense_number_prefix', label: 'Expense (รายการเบิก)', fallback: 'EX', sample: (p) => `${p}161026001` },
  { key: 'budget_number_prefix', label: 'Budget (งบประมาณ)', fallback: 'ST', sample: (p) => `${p}260001` },
];
const DOC_RE = /^[A-Z0-9]*$/;
// รหัส Marketing ต้องเป็นอังกฤษ 2 ตัวพอดี (ตรงกับที่ฟอร์มสร้างโอกาสตรวจ)
const MKT_RE = /^[A-Z]{2}$/;

const sameList = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// หน้า "รหัสเอกสาร" (/management/document-prefixes) — รหัสนำหน้าเลข Payment/Expense/Budget และรหัส Marketing
export default function DocumentPrefixSettings({ title }) {
  const [infoId, setInfoId] = useState(null);
  const [docs, setDocs] = useState({});
  const [savedDocs, setSavedDocs] = useState({});
  const [mktCodes, setMktCodes] = useState([]);
  const [savedMktCodes, setSavedMktCodes] = useState([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`${process.env.REACT_APP_API_URL}/information/`);
        setInfoId(res.data?.id || res.data?._id || null);
        const next = {};
        DOC_FIELDS.forEach((f) => { next[f.key] = res.data?.[f.key] || ''; });
        setDocs(next);
        setSavedDocs(next);
        const list = Array.isArray(res.data?.marketing_code_prefixes) ? res.data.marketing_code_prefixes : [];
        setMktCodes(list);
        setSavedMktCodes(list);
      } catch (e) {
        console.error('document prefixes load error', e);
      }
    })();
  }, []);

  const docsDirty = DOC_FIELDS.some((f) => (docs[f.key] || '') !== (savedDocs[f.key] || ''));
  const mktDirty = !sameList(mktCodes, savedMktCodes);

  const save = async (key, body, after) => {
    if (!infoId) return;
    setSavingKey(key);
    try {
      await api.put(`${process.env.REACT_APP_API_URL}/information/${infoId}`, body);
      after();
      showAlert('บันทึกแล้ว');
    } catch (e) {
      console.error('document prefixes save error', e);
      showAlert('บันทึกไม่สำเร็จ');
    } finally {
      setSavingKey('');
    }
  };

  const saveDocs = () => {
    const body = {};
    DOC_FIELDS.forEach((f) => { body[f.key] = docs[f.key] || ''; });
    save('docs', body, () => setSavedDocs(docs));
  };
  const saveMkt = () => save('mkt', { marketing_code_prefixes: mktCodes }, () => {
    setSavedMktCodes(mktCodes);
    setMarketingCodesCache(mktCodes.length ? mktCodes : DEFAULT_MARKETING_CODES);
  });

  const handleAdd = () => {
    const value = input.trim().toUpperCase();
    if (!value) return;
    if (!MKT_RE.test(value)) {
      setError('ต้องเป็นตัวอักษรอังกฤษ 2 ตัว เช่น MS');
      return;
    }
    if (mktCodes.includes(value)) {
      setError('มีรหัสนี้อยู่แล้ว');
      return;
    }
    setMktCodes((prev) => [...prev, value]);
    setInput('');
    setError('');
  };

  const effectiveMkt = mktCodes.length ? mktCodes : DEFAULT_MARKETING_CODES;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          ตั้งค่ารหัสนำหน้าเลขเอกสารและรหัส Marketing แต่ละข้อบันทึกแยกกัน ถ้าเว้นว่างระบบใช้ค่าเริ่มต้น
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        <SettingSection
          step={1}
          icon={ReceiptLongRoundedIcon}
          title="รหัสนำหน้าเลขเอกสารการเงิน"
          description="ตัวอักษรนำหน้าเลข Payment / Expense / Budget ที่ระบบสร้างให้อัตโนมัติ การเปลี่ยนรหัสมีผลกับเลขที่สร้างใหม่เท่านั้น เลขเดิมไม่เปลี่ยน"
          status={DOC_FIELDS.every((f) => !docs[f.key])
            ? 'ยังไม่ได้ตั้งค่า — ระบบใช้ STN / EX / ST เป็นค่าเริ่มต้น'
            : 'มีการกำหนดรหัสเอง (ช่องที่เว้นว่างใช้ค่าเริ่มต้น)'}
          isCustom={DOC_FIELDS.some((f) => docs[f.key])}
          dirty={docsDirty}
          saving={savingKey === 'docs'}
          disabled={!infoId}
          onSave={saveDocs}
          onReset={() => setDocs(savedDocs)}
        >
          <Stack spacing={2}>
            {DOC_FIELDS.map((f) => {
              const effective = docs[f.key] || f.fallback;

              return (
                <TextField
                  key={f.key}
                  size="small"
                  label={f.label}
                  placeholder={f.fallback}
                  value={docs[f.key] || ''}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    if (DOC_RE.test(v)) setDocs((prev) => ({ ...prev, [f.key]: v }));
                  }}
                  inputProps={{ maxLength: 6, style: { fontFamily: 'monospace', fontWeight: 700 } }}
                  helperText={`ตัวอย่างเลขที่จะออก: ${f.sample(effective)}`}
                  fullWidth
                />
              );
            })}
          </Stack>
        </SettingSection>

        <SettingSection
          step={2}
          icon={CampaignRoundedIcon}
          title="รหัส Marketing"
          description="รหัส 2 ตัวอักษรที่ใช้ตอนสร้างโอกาสใหม่ใน Marketing เลข Document No. จะเป็น รหัส_ปี เลขวิ่ง เช่น MS_260035 ถ้ามีรหัสเดียว ระบบเลือกให้เลย ถ้ามีหลายรหัสจะให้เลือกจาก dropdown"
          status={mktCodes.length
            ? `ใช้รหัส ${mktCodes.join(' / ')}`
            : `ยังไม่ได้ตั้งค่า — ระบบใช้ ${DEFAULT_MARKETING_CODES.join(' / ')} เป็นค่าเริ่มต้น`}
          isCustom={mktCodes.length > 0}
          dirty={mktDirty}
          saving={savingKey === 'mkt'}
          disabled={!infoId}
          onSave={saveMkt}
          onReset={() => { setMktCodes(savedMktCodes); setError(''); }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2, minHeight: 36 }}>
            {effectiveMkt.map((c) => (
              <Chip
                key={c}
                label={c}
                onDelete={mktCodes.length ? () => setMktCodes((prev) => prev.filter((x) => x !== c)) : undefined}
                variant={mktCodes.length ? 'filled' : 'outlined'}
                sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.85rem', height: 34 }}
              />
            ))}
          </Stack>
          <TextField
            size="small"
            placeholder="เพิ่มรหัสใหม่ เช่น MS"
            value={input}
            onChange={(e) => {
              setInput(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2));
              setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
            error={Boolean(error)}
            helperText={error || 'ตัวอักษรอังกฤษ 2 ตัว กด Enter เพื่อเพิ่ม'}
            sx={{ width: { xs: '100%', sm: 320 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button size="small" onClick={handleAdd} disabled={!input.trim()} startIcon={<AddRoundedIcon />}>
                    เพิ่ม
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </SettingSection>
      </Box>
    </Box>
  );
}
